import React from "react";
import styled, { css } from "styled-components";

import { Flex, JustifyContentOptions } from "../../components/Grid";
import { Headline, LargeTitle, Subhead } from "../../components/Typography";
import { useCountdown } from "../../hooks/countdown";
import {
  hideAboveMobile,
  hideOnMobile,
  xSmallScreen,
} from "../../lib/mediaQuery";
import { prefixZeroes } from "../../utils/math";

interface DropTimeProps {
  timestamp: number;
  className?: string;
  justify?: JustifyContentOptions;
}

export const DropTime: React.FC<DropTimeProps> = ({
  className,
  timestamp,
  justify = "center",
}) => {
  const dropCountdown = useCountdown(timestamp);
  if (
    dropCountdown.isComplete ||
    isNaN(dropCountdown.seconds) ||
    isNaN(dropCountdown.minutes) ||
    isNaN(dropCountdown.hours) ||
    isNaN(dropCountdown.days)
  ) {
    return null;
  }

  return (
    <DropContainer className={className}>
      <Flex direction={"column"} justify={justify} data-justify={justify}>
        <DropTimeText data-justify={justify}>Dropping:</DropTimeText>
      </Flex>
      <Flex direction={"row"} justify={justify} data-justify={justify}>
        <DropColumn>
          <DropMainText>{prefixZeroes(dropCountdown.days)}</DropMainText>
          <DropSubText>
            {`Day${dropCountdown.days !== 1 ? "s" : ""}`}
          </DropSubText>
        </DropColumn>
        <DropColonDivider />
        <DropColumn>
          <DropMainText>{prefixZeroes(dropCountdown.hours)}</DropMainText>
          <DropSubText>
            {`Hour${dropCountdown.hours !== 1 ? "s" : ""}`}
          </DropSubText>
        </DropColumn>
        <DropColonDivider />
        <DropColumn>
          <DropMainText>{prefixZeroes(dropCountdown.minutes)}</DropMainText>
          <DropSubText>
            {`Min${dropCountdown.minutes !== 1 ? "s" : ""}`}
          </DropSubText>
        </DropColumn>
        <DropColonDivider />
        <DropColumn>
          <DropMainText>{prefixZeroes(dropCountdown.seconds)}</DropMainText>
          <DropSubText>{`Secs`}</DropSubText>
        </DropColumn>
      </Flex>
    </DropContainer>
  );
};

const DropColonDivider: React.FC<{ empty?: boolean }> = ({ empty }) => (
  <>
    <LargeTitle
      css={`
        ${hideOnMobile}
      `}
    >
      {!empty ? (
        <span
          css={`
            margin: 0 var(--spacing-xs);
          `}
        >
          :
        </span>
      ) : (
        ""
      )}
    </LargeTitle>
    <Headline
      css={`
        ${hideAboveMobile}
        margin-top: 2px;
      `}
    >
      {!empty ? (
        <span
          css={`
            margin: 0 var(--spacing-xs);
          `}
        >
          :
        </span>
      ) : (
        ""
      )}
    </Headline>
  </>
);

const DropMainText = styled(LargeTitle)`
  color: rgb(var(--white-light));
  font-variant-numeric: tabular-nums;
  text-align: center;
  ${xSmallScreen(
    css`
      font-size: 20px;
      line-height: 30px;
      font-weight: 700;
    `
  )}
`;

const DropSubText = styled(Subhead)`
  color: rgb(var(--white-light));
  text-align: center;
`;

export const DropTimeText = styled(DropSubText)`
  margin-bottom: var(--spacing-xxs);
  text-align: left;
  [data-justify="center"] & {
    text-align: center;
  }
`;

const DropContainer = styled.div`
  opacity: 1;
  border-radius: var(--spacing-xxs);
  transition: background-color var(--transition-speed-normal) ease;
  max-width: 300px;
`;

const DropColumn = styled(Flex)`
  flex-direction: column;
  text-align: center;
`;
